"use client";

import * as React from "react";
import {
  AreaChart,
  BarChart3,
  BarChartHorizontal,
  CalendarDays,
  ChartLine,
  ChartNetwork,
  ChartPie,
  ChartScatter,
  CircleDot,
  FileText,
  Funnel,
  Gauge,
  Link2,
  Radar,
  Sparkles,
  Table2,
  Waypoints,
} from "lucide-react";

import styles from "./BlockTypePicker.module.css";

export type BlockKind =
  | "kpi"
  | "chart"
  | "line_chart"
  | "bar_chart"
  | "horizontal_bar_chart"
  | "pie_chart"
  | "donut_chart"
  | "funnel_chart"
  | "area_chart"
  | "stacked_bar_chart"
  | "treemap_chart"
  | "heatmap_chart"
  | "scatter_chart"
  | "sankey_chart"
  | "cohort_chart"
  | "waterfall_chart"
  | "gauge_chart"
  | "radar_chart"
  | "calendar_heatmap_chart"
  | "boxplot_chart"
  | "table"
  | "insight"
  | "text"
  | "embed";

type Choice = {
  kind: BlockKind;
  icon: React.ComponentType<{ size?: number; "aria-hidden"?: boolean }>;
  label: string;
  desc: string;
  example: string;
};

const CHOICE_GROUPS: Array<{ title: string; choices: Choice[] }> = [
  {
    title: "Most used",
    choices: [
      { kind: "kpi", icon: Gauge, label: "Number", desc: "One headline result", example: "Floats sent" },
      { kind: "bar_chart", icon: BarChart3, label: "Bar comparison", desc: "Compare people, clients, or pipeline stages", example: "Floats sent by recruiter" },
      { kind: "horizontal_bar_chart", icon: BarChartHorizontal, label: "Ranked list", desc: "Show best or worst groups first", example: "Recruiter leaderboard" },
      { kind: "line_chart", icon: ChartLine, label: "Line trend", desc: "See a number change over time", example: "Floats sent by week" },
      { kind: "pie_chart", icon: ChartPie, label: "Pie share", desc: "Show parts of one total", example: "Share by recruiter" },
      { kind: "donut_chart", icon: CircleDot, label: "Donut share", desc: "Share with a center total", example: "Share by recruiter" },
      { kind: "funnel_chart", icon: Funnel, label: "Funnel", desc: "Show drop-off across the steps you choose", example: "Float engagement funnel" },
      { kind: "table", icon: Table2, label: "List", desc: "Rows for comparison", example: "Recruiter leaderboard" },
    ],
  },
  {
    title: "More charts",
    choices: [
      { kind: "area_chart", icon: AreaChart, label: "Filled trend", desc: "Trend where the total matters", example: "Floats sent over time" },
      { kind: "stacked_bar_chart", icon: BarChart3, label: "Stacked bar", desc: "Break each bar into parts", example: "Recruiter by stage" },
      { kind: "treemap_chart", icon: Table2, label: "Tile share", desc: "Share shown with sized tiles", example: "Share by recruiter" },
      { kind: "heatmap_chart", icon: Table2, label: "Heat map", desc: "Find busy spots across two fields", example: "Recruiter by stage" },
      { kind: "scatter_chart", icon: ChartScatter, label: "Relationship", desc: "Compare two numbers as dots", example: "Sends vs reply rate" },
      { kind: "gauge_chart", icon: Gauge, label: "Gauge", desc: "One result against a scale", example: "Open rate gauge" },
      { kind: "radar_chart", icon: Radar, label: "Profile", desc: "Compare the numbers you choose", example: "Recruiter profile" },
      { kind: "calendar_heatmap_chart", icon: CalendarDays, label: "Calendar activity", desc: "Busy and quiet days", example: "Daily float activity" },
      { kind: "boxplot_chart", icon: Waypoints, label: "Spread", desc: "Range and middle of a number", example: "Floats sent spread" },
      { kind: "sankey_chart", icon: ChartNetwork, label: "Flow", desc: "Movement from one field to another", example: "Stage to channel" },
      { kind: "cohort_chart", icon: CalendarDays, label: "Cohort grid", desc: "Groups across periods or stages", example: "Recruiter by channel" },
      { kind: "waterfall_chart", icon: BarChart3, label: "Waterfall", desc: "Steps that add or subtract", example: "Engagement steps" },
    ],
  },
  {
    title: "Narrative",
    choices: [
      { kind: "insight", icon: Sparkles, label: "Insight", desc: "Generated analysis", example: "What changed and why" },
      { kind: "text", icon: FileText, label: "Text", desc: "Narrative or notes", example: "Executive summary" },
      { kind: "embed", icon: Link2, label: "Embed", desc: "Floats record card", example: "Candidate, float, contact, or job" },
    ],
  },
];

export interface BlockTypePickerProps {
  onSelect: (kind: BlockKind) => void;
}

export function BlockTypePicker({ onSelect }: BlockTypePickerProps) {
  return (
    <div className={styles.root} role="group" aria-label="Section types">
      {CHOICE_GROUPS.map((group) => (
        <section key={group.title} className={styles.group}>
          <div className={styles.groupTitle}>{group.title}</div>
          <div className={styles.grid}>
            {group.choices.map((c) => (
              <button
                key={c.kind}
                type="button"
                className={styles.choice}
                onClick={() => onSelect(c.kind)}
              >
                <span className={styles.icon}>
                  <c.icon size={18} aria-hidden={true} />
                </span>
                <span className={styles.copy}>
                  <span className={styles.label}>{c.label}</span>
                  <span className={styles.desc}>{c.desc}</span>
                  <span className={styles.example}>{c.example}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
